/**
 * Summary Statistics Panel Component
 * Implements PRD FR-38
 */

import { useSimulationStore } from '../state/simulationStore';
import { computeExpectedExcessReturn } from '../model/grossmanMillerEngine';

interface StatCardProps {
  label: string;
  value: string | number;
  sublabel?: string;
  color?: string;
  tooltip?: string;
}

function StatCard({ label, value, sublabel, color = 'text-gray-800' }: StatCardProps) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold ${color} mt-1`}>{value}</div>
      {sublabel && <div className="text-xs text-gray-500 mt-1">{sublabel}</div>}
    </div>
  );
}

export function SummaryStatsPanel() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          Run a simulation to see summary statistics
        </div>
      </div>
    );
  }

  const { equilibrium, params } = simulation;
  const {
    liquidityDiscount,
    immediacy,
    autocorrelation,
    S1,
    S2,
    endogenousN,
  } = equilibrium;
  const { i, n, c } = params;

  const expectedReturn = computeExpectedExcessReturn(params);
  const priceChange = S2 - S1;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary Statistics</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Liquidity Discount per Share */}
        <StatCard
          label="Discount/Share"
          value={liquidityDiscount.toFixed(4)}
          sublabel={`${((liquidityDiscount / params.mu) * 100).toFixed(2)}% of μ`}
          color="text-amber-600"
        />

        {/* Immediacy */}
        <StatCard
          label="Immediacy"
          value={`${(immediacy * 100).toFixed(1)}%`}
          sublabel={`${n}/(${n}+1)`}
          color="text-green-600"
        />

        {/* MM Expected Return */}
        <StatCard
          label="MM Expected Return"
          value={expectedReturn.total.toFixed(4)}
          sublabel={`${expectedReturn.perUnit.toFixed(4)}/unit`}
          color="text-purple-600"
        />

        {/* Price Autocorrelation */}
        <StatCard
          label="Autocorrelation"
          value={autocorrelation.toFixed(4)}
          sublabel="q = -t/(1+t)"
          color={autocorrelation < 0 ? 'text-red-600' : 'text-gray-600'}
        />
      </div>

      {/* Additional Stats */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Actual Price Change */}
        <StatCard
          label="Actual ΔP (S₂ - S₁)"
          value={priceChange.toFixed(4)}
          color={priceChange >= 0 ? 'text-green-600' : 'text-red-600'}
        />

        {/* Total Volume */}
        <StatCard
          label="Volume at t=1"
          value={((n * i) / (n + 1)).toFixed(2)}
          sublabel="units traded"
        />

        {/* Endogenous N */}
        {c > 0 && endogenousN !== undefined && (
          <StatCard
            label="Equilibrium n*"
            value={endogenousN}
            sublabel={`with c = ${c}`}
            color="text-blue-600"
          />
        )}
      </div>

      {/* Autocorrelation Explanation */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-700">
          About Price Autocorrelation
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Negative autocorrelation ({autocorrelation.toFixed(4)}) arises because price
          pressure from inventory imbalance at t=1 reverses at t=2 when positions unwind.
          The t parameter = {equilibrium.autocorrelationT.toFixed(6)}.
        </p>
      </div>
    </div>
  );
}
